/* ================================================================
   bm-sticky-atc.js
   Sticky Add to Cart bar — mirrors the main product form.

   Strategy:
   - IntersectionObserver watches the main ATC button.
     Shows sticky when button leaves viewport, hides when it returns.
   - MutationObserver watches main button (text + disabled state)
     and the price block (#price-<sectionId>).
   - Click on sticky button triggers a click on the real main button,
     which lets product-form.js handle the cart submission normally.
   - Listens for .qb-card__input changes (quantity-bundle) with a
     small debounce so the sync fires after the DOM is updated.

   No parallel form, no duplicate inputs, no fetch calls.
   ================================================================ */

(function () {
  'use strict';

  function initStickyAtc(el) {
    var sectionId = el.dataset.sectionId;
    if (!sectionId) return;

    /* ── Find section root ─────────────────────────────────────── */
    var sectionRoot =
      document.getElementById('MainProduct-' + sectionId) ||
      el.closest('.shopify-section') ||
      document.body;

    /* ── Main submit button (by specific ID, fallback to class) ── */
    var mainBtn =
      sectionRoot.querySelector('#ProductSubmitButton-' + sectionId) ||
      sectionRoot.querySelector('.product-form__submit');

    if (!mainBtn) return;

    /* ── Price block updated by quantity-bundle.js ─────────────── */
    var priceBlock = document.getElementById('price-' + sectionId);

    /* ── Sticky elements ────────────────────────────────────────── */
    var stickyBtn     = el.querySelector('[data-satc-btn]');
    var stickyBtnText = el.querySelector('[data-satc-btn-text]');
    var stickyBtnQty  = el.querySelector('[data-satc-btn-qty]');
    var stickyPrice   = el.querySelector('[data-satc-price]');
    var stickyCompare = el.querySelector('[data-satc-compare]');
    var stickyImage   = el.querySelector('[data-satc-image]');

    if (!stickyBtn || !stickyBtnText) return;

    /* ── IntersectionObserver — show/hide based on main btn ─────── */
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hide();
          } else {
            show();
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    io.observe(mainBtn);

    /* ── MutationObserver — sync state from main button ────────── */
    var btnObs = new MutationObserver(function () { syncFromMain(); });
    btnObs.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      subtree: true,
      childList: true,
      characterData: true,
    });

    /* ── MutationObserver — sync price when quantity-bundle fires ─ */
    if (priceBlock) {
      var priceObs = new MutationObserver(function () { syncPrice(); });
      priceObs.observe(priceBlock, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    }

    /* ── quantity-bundle radio change — debounced sync ─────────── */
    var syncTimer = null;
    sectionRoot.querySelectorAll('.qb-card__input').forEach(function (input) {
      input.addEventListener('change', function () {
        clearTimeout(syncTimer);
        /* 60ms delay lets quantity-bundle.js finish updating the DOM */
        syncTimer = setTimeout(syncFromMain, 60);
      });
    });

    /* ── Sticky button click → trigger real form submission ─────── */
    stickyBtn.addEventListener('click', function () {
      if (!mainBtn.disabled) {
        mainBtn.click();
      }
    });

    /* ── Initial sync ───────────────────────────────────────────── */
    syncFromMain();

    /* ── Helpers ────────────────────────────────────────────────── */

    function show() {
      el.classList.add('bm-satc--visible');
      el.setAttribute('aria-hidden', 'false');
    }

    function hide() {
      el.classList.remove('bm-satc--visible');
      el.setAttribute('aria-hidden', 'true');
    }

    function syncFromMain() {
      /* Disabled state */
      if (mainBtn.disabled) {
        stickyBtn.setAttribute('disabled', '');
      } else {
        stickyBtn.removeAttribute('disabled');
      }

      /* Button text — split base CTA from quantity suffix into two lines.
         quantity-bundle.js injects " (N unidades)" into the main button span. */
      var textSpan = mainBtn.querySelector('span:not(.loading__spinner)');
      if (textSpan) {
        var rawText  = textSpan.textContent.trim();
        var parenIdx = rawText.indexOf(' (');

        if (parenIdx > 0) {
          stickyBtnText.textContent = rawText.slice(0, parenIdx).trim();
          if (stickyBtnQty) {
            stickyBtnQty.textContent = rawText.slice(parenIdx).trim();
            stickyBtnQty.style.display = '';
          }
        } else {
          stickyBtnText.textContent = rawText;
          if (stickyBtnQty) stickyBtnQty.style.display = 'none';
        }
      }

      syncPrice();
      syncImage();
    }

    function syncPrice() {
      if (!priceBlock || !stickyPrice) return;

      var saleEl    = priceBlock.querySelector('.price-item--sale');
      var regularEl = priceBlock.querySelector('s .price-item--regular, s.price-item--regular');

      if (saleEl) {
        /* On sale: show sale price + strikethrough compare-at */
        var saleText = saleEl.textContent.trim();
        if (saleText) stickyPrice.textContent = saleText;

        if (stickyCompare) {
          /* Try to find the <s> compare-at element */
          var compareEl = priceBlock.querySelector('s .price-item--regular') ||
                          priceBlock.querySelector('s.price-item--regular');
          if (!compareEl) {
            /* Fallback: look for any <s> inside the price block */
            var sTag = priceBlock.querySelector('s');
            if (sTag) compareEl = sTag;
          }
          if (compareEl) {
            var compareText = compareEl.textContent.trim();
            if (compareText) {
              stickyCompare.textContent = compareText;
              stickyCompare.style.display = '';
            }
          } else {
            stickyCompare.style.display = 'none';
          }
        }
      } else {
        /* No sale: show regular price, hide compare */
        var regEl = priceBlock.querySelector('.price-item--regular');
        if (regEl) {
          var regText = regEl.textContent.trim();
          if (regText) stickyPrice.textContent = regText;
        }
        if (stickyCompare) stickyCompare.style.display = 'none';
      }
    }

    function syncImage() {
      if (!stickyImage) return;

      var checkedBundleInput = sectionRoot.querySelector('.qb-card__input:checked');
      if (!checkedBundleInput) return;

      var imageSrc = checkedBundleInput.getAttribute('data-qb-image-thumb');
      if (!imageSrc) {
        imageSrc = checkedBundleInput.getAttribute('data-qb-image-src');
      }
      if (!imageSrc) return;

      var imageSrcset = checkedBundleInput.getAttribute('data-qb-image-thumb-srcset');
      if (!imageSrcset) {
        imageSrcset = checkedBundleInput.getAttribute('data-qb-image-srcset');
      }
      var imageAlt = checkedBundleInput.getAttribute('data-qb-image-alt');

      stickyImage.setAttribute('src', imageSrc);
      if (imageSrcset) stickyImage.setAttribute('srcset', imageSrcset);
      if (imageAlt) stickyImage.setAttribute('alt', imageAlt);
    }
  }

  /* ── Bootstrap: init all sticky bars on the page ─────────────── */
  function init() {
    document.querySelectorAll('[id^="BmStickyAtc-"]').forEach(initStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
